import Button from "@/common/components/Button"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import { ChevronsRightIcon, InfoIcon } from "lucide-react"
import { useState } from "react"
import { useParams } from "react-router-dom"
import { usePosMachineById } from "../../hooks/usePosMachineById"
import AssignPosModal from "./components/AssignPosModal"
import BlockPosModal from "./components/BlockPosModal"
import PendingScreenPosShow from "./components/PendingScreenPosShow"
import ReleasePosModal from "./components/ReleasePosModal"
import UnblockPosModal from "./components/UnblockPosModal"
import GoBackButton from "@/common/components/GoBackButton";

const PosShow = () => {
  const { id } = useParams()

  const [isBlockModalOpen, setBlockModalOpen] = useState(false)
  const [isUnblockModalOpen, setUnblockModalOpen] = useState(false)
  const [isAssignModalOpen, setAssignModalOpen] = useState(false)
  const [isReleaseModalOpen, setReleaseModalOpen] = useState(false)

  const {
    data: posById,
    isPending: posByIdIsPending,
    error: posByIdError = {} as any,
    refetch: refetchPosById,
  } = usePosMachineById(id!)

  const infoBlocks = [
    {
      label: "Modelo POS",
      value: posById?.model.display_name,
    },
    {
      label: "Código do POS",
      value: posById?.reference_number,
    },
    {
      label: "Estabelecimento",
      value: posById?.establishment_company_branch?.general_information.fantasy_name || posById?.lead?.display_name,
    },
  ]

  if (posByIdIsPending) {
    return <PendingScreenPosShow />
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback="/pos" searchLastRouteFromFallback />

          <p className="text-white-400 font-normal">POS</p>

          <ChevronsRightIcon color="#ffffffb3" />

          <h2 className="font-bold">POS {posById?.reference_number}</h2>
        </div>
      </div>
      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={posByIdError?.status}
        refetchFunction={refetchPosById}
      >
        <div className="bg-[#FFFFFF0D] w-full rounded-lg p-6 flex flex-col gap-4">
          {posById?.status === "in_stock" && (
            <div className="bg-[#FFFFFF0D] w-full p-2 flex flex-row justify-between overflow-hidden">
              <div className="flex flex-row gap-2">
                <InfoIcon className="w-[28px]" strokeWidth={1} color="var(--warning)" />
                <div>
                  <h2 className="font-bold text-white text-[13px] m-0">
                    Associe esse POS a um Estabelecimento de maneira avulsa
                  </h2>
                  <h2 className="text-white text-[11px] italic m-0 overflow-hidden text-ellipsis whitespace-nowrap">
                    Caso algo tenha acontecido com o POS ou deseje associar de maneira avulsa a um estabelecimento.
                  </h2>
                </div>
              </div>
              <Button
                className={
                  "py-2 px-4 bg-primary-blue rounded-sm cursor-pointer hover:bg-[section.buttonColor] whitespace-nowrap"
                }
                onClick={() => setAssignModalOpen(true)}
              >
                Associar POS a Estabelecimento
              </Button>
            </div>
          )}
          {posById?.status !== "blocked" && (
            <div className="bg-[#FFFFFF0D] w-full p-2 flex flex-row justify-between overflow-hidden">
              <div className="flex flex-row gap-2">
                <InfoIcon className="w-[28px]" strokeWidth={1} color="var(--warning)" />
                <div>
                  <h2 className="font-bold text-white text-[13px] m-0">
                    O POS será bloqueado e ficará impedido de ser acessado e realizar transações
                  </h2>
                  <h2 className="text-white text-[11px] italic m-0 overflow-hidden text-ellipsis whitespace-nowrap">
                    Caso o POS esteja associado a um estabelecimento o mesmo não poderá utilizar o POS.
                  </h2>
                </div>
              </div>
              <Button
                className={"py-2 bg-danger rounded-sm cursor-pointer hover:bg-[section.buttonColor] whitespace-nowrap"}
                onClick={() => setBlockModalOpen(true)}
                operationKlassToCheckPermission="Admin::Pos::Machines::Block"
              >
                Bloquear
              </Button>
            </div>
          )}
          {posById?.status === "blocked" && (
            <div className="bg-[#FFFFFF0D] w-full p-2 flex flex-row justify-between overflow-hidden">
              <div className="flex flex-row gap-2">
                <InfoIcon className="w-[28px]" strokeWidth={1} color="var(--warning)" />
                <div>
                  <h2 className="font-bold text-white text-[13px] m-0">
                    O POS será desbloqueado e ficará livre para ser acessado e realizar transações
                  </h2>
                  <h2 className="text-white text-[11px] italic m-0 overflow-hidden text-ellipsis whitespace-nowrap">
                    Caso o POS esteja associado a um estabelecimento o mesmo poderá utilizar o POS.
                  </h2>
                </div>
              </div>
              <Button
                className={
                  "py-2 px-4 bg-primary-blue rounded-sm cursor-pointer hover:bg-[section.buttonColor] whitespace-nowrap"
                }
                onClick={() => setUnblockModalOpen(true)}
                operationKlassToCheckPermission="Admin::Pos::Machines::Unblock"
              >
                Desbloquear POS
              </Button>
            </div>
          )}
          {(posById?.status === "associated" || posById?.status === "active") && (
            <div className="bg-[#FFFFFF0D] w-full p-2 flex flex-row justify-between overflow-hidden">
              <div className="flex flex-row gap-2 content-center items-center">
                <InfoIcon className="w-[28px]" strokeWidth={1} color="var(--warning)" />
                <div>
                  <h2 className="font-bold text-white text-[13px] ">Libere esse POS</h2>
                </div>
              </div>
              <Button
                className={
                  "py-2 px-4 bg-primary-blue rounded-sm cursor-pointer hover:bg-[section.buttonColor] whitespace-nowrap"
                }
                onClick={() => setReleaseModalOpen(true)}
              >
                Liberar POS
              </Button>
            </div>
          )}

          {infoBlocks.map((block, index) => (
            <div
              key={index}
              className="bg-[#FFFFFF1A] w-full h-[93px] rounded-lg p-3 flex flex-col justify-center overflow-hidden"
            >
              <h2 className="font-bold text-white text-[16px] m-0">{block.label}</h2>
              <h2 className="text-white text-[14px] m-0 overflow-hidden text-ellipsis whitespace-nowrap">
                {block.value || "Não informado"}
              </h2>
            </div>
          ))}
        </div>
        <BlockPosModal isOpen={isBlockModalOpen} setIsOpen={setBlockModalOpen} />
        <UnblockPosModal isOpen={isUnblockModalOpen} setIsOpen={setUnblockModalOpen} />
        <AssignPosModal isOpen={isAssignModalOpen} setIsOpen={setAssignModalOpen} />
        <ReleasePosModal isOpen={isReleaseModalOpen} setIsOpen={setReleaseModalOpen} />
      </ErrorMessageWrapper>
    </div>
  )
}

export default PosShow
